import { useMemo } from "react";
import { useEmployees } from "./useEmployees";
import { useShops } from "./useShops";
import { useSchedules } from "./useSchedules";
import { useOrganization } from "../context/OrganizationContext";
import {
  DashboardData,
  TodayOverview,
  WeekSummary,
  EmployeeStatistics,
  ScheduleHealth,
  QuickAction,
  ShopPerformance,
} from "../types/dashboard.types";
import {
  calculateTodayOverview,
  calculateWeekSummary,
  calculateEmployeeStats,
  calculateScheduleHealth,
  calculateQuickActions,
  calculateShopPerformance,
  calculateTodayStaff,
} from "../utils/dashboardCalculations";
import {
  getWeekStart,
  getWeekDates,
  formatDate,
} from "../utils/scheduleHelpers";

/**
 * Custom hook for dashboard data
 * Aggregates data from employees, shops, and schedules hooks
 * All calculations are memoized for performance
 */
export function useDashboard(): DashboardData {
  // Calculate date range for the current week (needed for dashboard)
  const dateRange = useMemo(() => {
    const now = new Date();
    const weekStart = getWeekStart(now);
    const weekDates = getWeekDates(weekStart);
    const startDate = formatDate(weekDates[0]);
    const endDate = formatDate(weekDates[weekDates.length - 1]);
    return { startDate, endDate };
  }, []);

  // Compose existing hooks
  const { organization } = useOrganization();
  const {
    employees,
    isLoading: isLoadingEmployees,
    error: employeesError,
    refetch: refetchEmployees,
  } = useEmployees();
  const {
    shops,
    isLoading: isLoadingShops,
    error: shopsError,
    refetch: refetchShops,
  } = useShops();
  // Check if we have enough data to show the full dashboard
  const hasShops = shops.length > 0;
  const hasEmployees = employees.length > 0;
  const hasData = hasShops && hasEmployees;

  // Only fetch schedules if there are shops (schedules belong to shops)
  const shouldFetchSchedules = !isLoadingShops && shops.length > 0;
  const {
    schedules,
    isLoading: isLoadingSchedules,
    error: schedulesError,
    refetch: refetchSchedules,
  } = useSchedules(
    undefined,
    undefined,
    shouldFetchSchedules ? dateRange : undefined
  );

  // Get availability mode from organization settings
  const availabilityMode = organization?.settings?.availabilityMode || "daily";

  // Combined loading and error states
  // Don't wait for schedules if there's no data to display
  const isLoadingBase = isLoadingEmployees || isLoadingShops;
  const isLoading = isLoadingBase || (hasData && isLoadingSchedules);
  const error = employeesError || shopsError || schedulesError;

  // Memoized calculations
  const todayOverview: TodayOverview = useMemo(
    () => calculateTodayOverview(employees, shops, schedules),
    [employees, shops, schedules]
  );

  const weekSummary: WeekSummary = useMemo(
    () => calculateWeekSummary(employees, shops, schedules),
    [employees, shops, schedules]
  );

  const employeeStats: EmployeeStatistics = useMemo(
    () => calculateEmployeeStats(employees, shops),
    [employees, shops]
  );

  const scheduleHealth: ScheduleHealth = useMemo(
    () => calculateScheduleHealth(employees, shops, schedules),
    [employees, shops, schedules]
  );

  const quickActions: QuickAction[] = useMemo(
    () => calculateQuickActions(employees, shops, schedules),
    [employees, shops, schedules]
  );

  const shopPerformance: ShopPerformance[] = useMemo(
    () => calculateShopPerformance(employees, shops, schedules),
    [employees, shops, schedules]
  );

  const todayStaff = useMemo(
    () => calculateTodayStaff(employees, shops, schedules),
    [employees, shops, schedules]
  );

  // Combined refetch function
  const refetch = () => {
    refetchEmployees();
    refetchShops();
    refetchSchedules();
  };

  return {
    isLoading,
    error: error as Error | null,
    todayOverview,
    weekSummary,
    employeeStats,
    scheduleHealth,
    quickActions,
    shopPerformance,
    todayStaff,
    hasData,
    hasShops,
    hasEmployees,
    refetch,
    availabilityMode,
    rawData: {
      employees,
      shops,
      schedules,
    },
  };
}
